"use client";

import { CheckIcon, ChevronDownIcon, SearchIcon } from "lucide-react";
import { useState } from "react";
import { Item, ItemContent, ItemTitle } from "../atoms/item";
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/popover";
import { cn } from "../lib";
import {
  SearchList,
  type SearchListItem,
  type SearchListSection,
} from "./search-list";

/** One option in a pick-one list: a title, and a tick when it's the current one. */
export function SearchSelectRow({
  selected,
  title,
  ...rest
}: React.ComponentProps<typeof Item> & {
  selected: boolean;
  title: React.ReactNode;
}) {
  return (
    // The command palette supplies its own tighter padding, which wins the
    // class merge, so the row height is set here rather than inherited: a
    // min-height cannot be undone by whichever padding happens to win.
    <Item
      size="sm"
      {...rest}
      className={cn("pointer-coarse:min-h-11", rest.className)}
    >
      <ItemContent>
        <ItemTitle className="truncate">{title}</ItemTitle>
      </ItemContent>
      {selected && (
        <>
          <CheckIcon className="text-accent size-4 shrink-0" />
          <span className="sr-only">(selected)</span>
        </>
      )}
    </Item>
  );
}

/**
 * Pick one thing from a list too long to scroll: a trigger showing the current
 * choice, and a `SearchList` behind it.
 *
 * The pick-one counterpart to `MultiSelectField`. Both exist so that no screen
 * has to hand-roll a combobox again — the versions that were hand-rolled
 * reimplemented click-outside, focus management and filtering per screen, and
 * quietly drifted (one shipped a focus ring in a colour that isn't a token, so
 * it had no focus ring at all).
 */
export function SearchSelect<T extends SearchListItem>({
  items,
  selectedId,
  onSelect,
  match,
  renderRow,
  renderTriggerLabel,
  sections,
  sectionOf,
  placeholder = "Select...",
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: {
  items: readonly T[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  match: (item: T, query: string) => boolean;
  renderRow: (item: T, state: { selected: boolean }) => React.ReactNode;
  /** How the current choice reads on the closed trigger. */
  renderTriggerLabel: (item: T) => React.ReactNode;
  sections?: readonly SearchListSection[];
  sectionOf?: (item: T) => string;
  /** Shown on the trigger when nothing is chosen. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedItem = items.find((item) => item.id === selectedId);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          "border-border bg-background flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm transition-colors",
          "pointer-coarse:h-11",
          "hover:bg-surface-hover focus-visible:border-accent focus-visible:ring-accent/40 focus-visible:ring-2 focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {/*
            The field's name as part of the trigger's content, not as an
            `aria-label` on it: a label attribute replaces the descendant text
            during accessible-name calculation, so the control announced itself
            as "Proposal" and never said which proposal was selected.
          */}
          {ariaLabel && <span className="sr-only">{ariaLabel}</span>}
          <SearchIcon className="text-muted-foreground size-4 shrink-0" />
          <span
            className={cn("truncate", !selectedItem && "text-muted-foreground")}
          >
            {selectedItem ? renderTriggerLabel(selectedItem) : placeholder}
          </span>
        </span>
        <ChevronDownIcon
          className={cn(
            "text-muted-foreground size-4 shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </PopoverTrigger>
      <PopoverContent
        // Matches the trigger, so the options line up under what opened them —
        // but a narrow trigger shouldn't produce a cramped list, and the floor
        // for that must still fit a 280px screen.
        className="w-(--anchor-width) min-w-[min(18rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] p-0"
        align="start"
      >
        <SearchList
          items={items}
          match={match}
          renderItem={renderRow}
          selectedIds={selectedId ? new Set([selectedId]) : undefined}
          sections={sections}
          sectionOf={sectionOf}
          placeholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          autoFocus
          onSelect={(id) => {
            onSelect(id);
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
